import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonVariant,
  IconName,
  ButtonIconAlign,
  CheckBox,
  Link,
  LabelAlign,
  LabelPosition,
  SelectorVariant,
  Label,
  LabelSize,
  DatePicker,
  DatePickerSize,
  TextInput,
  TextInputWidth,
  TextInputSize,
  Icon,
  IconSize
} from '@eightfold.ai/octuple';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router';
import Header from '../commons/Header';
import { actionButtonStyle } from '../commons/commonStyles';
import Footer from '../commons/Footer';
import Api from '../Api';
import { globalContext } from '../store';
import {
  getBrandValues,
  getBrandImages,
  checkDropDownValues,
  checkSubDropDownValues,
  updateDemographicData,
  validateDynamicForm,
  resolveError,
  trimTextFields,
} from '../commons/commonFunctions';
import { clientInfo } from '../commons/ClientDynamicInfo';

const WELCOME_BACK_TITLE = 'Welcome back';
const WELCOME_BACK_MESSAGE = 'We have a couple of things we need to update before bringing you to your leader portal.';
const UPDATED_PRIVACY_POLICY_TITLE = 'Updated Privacy Policy';
const UPDATED_PRIVACY_POLICY_MESSAGE = "We've updated our privacy policy. Please review it and accept it to continue.";
const ASSESSMENT_POLICY_LINK_TEXT = 'Heidrick Assessment Privacy Policy';
const ADDITIONAL_ACCOUNT_INFO_TITLE = 'Additional Account Information';
const ADDITIONAL_ACCOUNT_INFO_MESSAGE = 'Please fill out the information below.';
const CONTINUE_BUTTON_TEXT = 'Continue';

const GenerateTextField = (props:any) => {
    const { fieldKey, fieldValue, errors, onChange } = props;
    return (
      <>
        <TextInput
          id={fieldKey}
          clearable
          maxlength={100}
          minlength={0}
          name={fieldKey}
          onChange={onChange}
          value={fieldValue}
          inputWidth={TextInputWidth.fill}
          classNames='inputTextField'
          status={errors?.[`${fieldKey}Status`] ? 'error' : 'validating'}
          size={TextInputSize.Large}
        />
        {errors?.[`${fieldKey}Msg`] && (
          <span className='errorMessage'>{errors?.[`${fieldKey}Msg`]}</span>
        )}                  
      </>
    );
  }

const WelcomeBackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalState, dispatch } = useContext(globalContext);
  const [leftPanelImg, setLeftPanelImg] = useState<string>('');
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [showLoader, setShowLoader] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [dropdownOptions, setDropdownOptions] = useState<{ [key: string]: any }>({});
  const [privacyPolicyChecked, setPrivacyPolicyCheck] = useState(false);
  const [,setErrorMsg] = useState(false);
  const [showDownloadLoader, setShowDownloadLoader] = useState(false);
  const PRIVACY_POLICY_ATTACHMENTID = process.env.REACT_APP_PRIVACY_POLICY_ATTACHMENTID;

  // State object to hold all field values
  const [fieldValues, setFieldValues] = useState<{ [key: string]: any }>({});
  
  // Job sub-function related states
  const [jobSubFuncOptions, setJobSubFuncOptions] = useState<any>([]);
  const [jobSubFuncTxt, setJobSubFuncTxt] = useState('');
  const [disableSubFunc, setDisableSubFunc] = useState(true);
  const [industrySecTxt, setindustrySecTxt] = useState('');
  const [industrySectOptions, setIndustrySectOptions] = useState<any>([]);
  const [disableIndustrySec, setDisableIndustrySec] = useState(true);
  const [, setStartDate] = useState<any>('');
  const [, setEndDate] = useState<any>('');
  const [unformattedStartDate, setUnformattedStartDate] = useState<any>(null);
  const [unformattedEndDate, setUnformattedEndDate] = useState<any>(null);
  const otherTxt = 'Other (please specify)';
  const otherOptionList = [{ text: `${otherTxt}`, value: `${otherTxt}` }];

  const title = globalState?.clientData?.name;
  const nonFilledFields = location.state?.nonFilledFields || [];
  const acceptedDate = location.state?.acceptedDate;
  const lastUpdatedDate = location.state?.lastUpdatedDate;
  const demographicFieldsInfo = location.state?.demographicFieldsInfo || {
    requiredCount: 0,
    filledCount: 0,
    nonFilledCount: 0,
  };

  const isPrivacyPolicyEnabled = (lastUpdatedDate > acceptedDate);
  const isDemographicEnabled = (demographicFieldsInfo?.requiredCount > demographicFieldsInfo?.filledCount);

  const countryArrEth = ['United States of America (the)', 'Canada', 'Australia', 'United Kingdom of Great Britain and Northern Ireland (the)'];
  const countryArrLGBTQ = ['United States of America (the)', 'United Kingdom of Great Britain and Northern Ireland (the)'];
  const countryArrVeteranStatus = ['United States of America (the)'];
  const fieldArr = [
    { fieldName: 'What is your job title?', key: 'jobTitle', type: 'textField' },
    { fieldName: 'What is your job level?', key: 'jobLevel', type: 'dropDown' },
    { fieldName: 'Start Date', key: 'hireDate', type: 'date' },
    { fieldName: 'End Date', key: 'terminationDate', type: 'date' },
    { fieldName: 'How many years total have you been at your current job level?', key: 'yearsAtJobLevel', type: 'dropDown' },
    { fieldName: 'What is your job function?', key: 'jobFunction', type: 'dropDown' },
    { fieldName: 'What is your job sub-function?', key: 'jobSubFunction', type: 'dropDown' },
    { fieldName: 'How many years have you been in a leadership role?', key: 'yearsInLeadershipRole', type: 'dropDown' },
    { fieldName: 'How many people directly report to you?', key: 'directReports', type: 'dropDown' },
    { fieldName: 'How large is the organization under your leadership?', key: 'orgSizeUnderLeader', type: 'dropDown' },
    { fieldName: 'What industry do you work in?', key: 'industry', type: 'dropDown' },
    { fieldName: 'What industry sector do you work in?', key: 'industrySector', type: 'dropDown' },
    { fieldName: 'What type of organization do you work for?', key: 'orgType', type: 'dropDown' },
    { fieldName: 'What is the organizational structure of your company?', key: 'orgStructure', type: 'dropDown' },
    { fieldName: 'What is the size of your organization?', key: 'orgSize', type: 'dropDown' },
    { fieldName: 'What cycle/stage is your organization currently in?', key: 'orgCycle', type: 'dropDown' },
    { fieldName: 'How much revenue does your company generate annually?', key: 'orgRevenue', type: 'dropDown' },
    { fieldName: 'What type of environment do you work in?', key: 'workingEnvironment', type: 'dropDown' },
    { fieldName: 'What is your country of residence?', key: 'countryOfResidence', type: 'dropDown' },
    { fieldName: 'What is your ethnicity?', key: 'ethnicity', type: 'dropDown' },
    { fieldName: 'What is your gender?', key: 'gender', type: 'dropDown' },
    { fieldName: 'How do you identify?', key: 'lgbtq', type: 'dropDown' },
    { fieldName: 'Do you identify as being a part of any underrepresented group?', key: 'diversityNonSpecific', type: 'dropDown' },
    { fieldName: 'Did you serve on active duty in the U.S. Military?', key: 'veteranStatus', type: 'dropDown' },
    { fieldName: 'What is your Age?', key: 'age', type: 'dropDown' },
    { fieldName: 'What is the highest level of education you have completed?', key: 'education', type: 'dropDown' },
  ];
  const itemRefs = useRef(new Map());

  // Initialize fieldValues with all non-filled fields
  useEffect(() => {
    if (nonFilledFields.length > 0) {
      const initialFieldValues: { [key: string]: any } = {};
      nonFilledFields.forEach((fieldKey: string) => {
        const fieldInfo = fieldArr.find(f => f.key === fieldKey);
        // Get existing value from globalState if available
        const existingValue = globalState?.leaderData?.demographicSurveyData?.[fieldKey];

        // Initialize based on field type
        switch (fieldInfo?.type) {
          case 'textField':
            initialFieldValues[fieldKey] = existingValue || '';
            break;
          case 'dropDown':
            initialFieldValues[fieldKey] = existingValue || '';
            break;
          case 'date':
            initialFieldValues[fieldKey] = existingValue || null;
            // Initialize date states for hireDate and terminationDate
            if (fieldKey === 'hireDate' && existingValue) {
              setUnformattedStartDate(dayjs(existingValue));
              setStartDate(existingValue);
            } else if (fieldKey === 'terminationDate' && existingValue) {
              setUnformattedEndDate(dayjs(existingValue));
              setEndDate(existingValue);
            }
            break;
        }
      });
      setFieldValues(initialFieldValues);
    }
  }, [nonFilledFields, globalState?.lookUpData]);

  // Populate dropdown options only for non-filled fields
  useEffect(() => {
    if (nonFilledFields.length > 0 && globalState?.lookUpData) {
      const fieldToLookupMap: { [key: string]: string } = {
        jobLevel: 'Job Level',
        yearsAtJobLevel: 'Years at Job Level',
        yearsInLeadershipRole: 'Years in Leadership Role',
        directReports: 'Direct Reports',
        orgSizeUnderLeader: 'Org Size Under Leadership',
        jobFunction: 'Job Function',
        jobSubFunction: 'Job Sub-Function',
        industry: 'Industry',
        industrySubSector: 'Industry Sector',
        orgType: 'Org Type',
        orgStructure: 'Org Structure',
        orgCycle: 'Cycle/Org Cycle',
        orgSize: 'Org Size',
        orgRevenue: 'Revenue',
        workingEnvironment: 'Working Environment',
        countryOfResidence: 'Country of Residence',
        gender: 'Gender',
        ethnicity: 'Ethnicity',
        lgbtq: 'LGBTQ',
        diversityNonSpecific: 'Diversity, Non-Specific',
        veteranStatus: 'Veteran Status',
        education: 'Education',
        age: 'Age',
      };

      const newDropdownOptions: { [key: string]: any } = {};
      nonFilledFields.forEach((fieldKey: string) => {
        const lookupName = fieldToLookupMap[fieldKey];
        if (lookupName) {
          newDropdownOptions[fieldKey] = checkDropDownValues(lookupName, globalState?.lookUpData);
        }
      });

      setDropdownOptions(newDropdownOptions);
    }
  }, [nonFilledFields, demographicFieldsInfo, globalState?.lookUpData]);

  const changeSubFunction = (value: any) => {
    const jobSubFunction = fieldValues['jobSubFunction'] || value;
    if (jobSubFunction !== 'Not Applicable') {
      setFieldValues(prevValues => ({
        ...prevValues,
        jobSubFunction: value,
      }));
    }
    
    if (value === otherTxt) {
      showHideDescriptionField('jobSubFuncDescribe', 'block');
      setFieldValues(prevValues => ({
        ...prevValues,
        jobSubFuncDescribe: '',
      }));
    } else {
      showHideDescriptionField('jobSubFuncDescribe', 'none');
    }
  };

  const changeIndustrySector = (value: any) => {
    const industrySector = fieldValues['industrySector'] || value;

    if (industrySector !== 'Not Applicable') {
      setFieldValues(prevValues => ({
        ...prevValues,
        industrySector: value,
      }));
    }

    if (value === otherTxt) {
      showHideDescriptionField('industrySecDescribe', 'block');
      setFieldValues(prevValues => ({
        ...prevValues,
        industrySecDescribe: '',
      }));
    } else {
      showHideDescriptionField('industrySecDescribe', 'none');
    }
  };

  const showHideDescriptionField = (fieldKey: string, type: string) => {
    const element = itemRefs.current.get(fieldKey);
    if (!element) return;
    element.style.display = type;
  }

  const handleFieldChange = (key: string, value: any) => {
    resolveError(key, errors, setErrors);
    // Special handling for jobFunction field
    if (key === 'jobFunction') {

      const options = checkSubDropDownValues(value, 'Job Sub-Function', globalState?.lookUpData);
      if (value && options && options?.length > 0) {
        setJobSubFuncTxt('');

        if (value === otherTxt) {
          setJobSubFuncOptions(otherOptionList);
          setDisableSubFunc(false);
          if (fieldValues.jobSubFunction && fieldValues.jobSubFunction?.includes('~')) {
            setJobSubFuncTxt(fieldValues.jobSubFunction.replace('OTH~', ''));
          }
          // Auto-set jobSubFunction to 'Other' when 'Other' is selected for jobFunction
          setFieldValues(prevValues => ({
             ...prevValues,
             [key]: value,
            jobSubFunction: '',
          }));
        } else {
          setJobSubFuncOptions(options);
          setDisableSubFunc(false);
          setJobSubFuncTxt('');
          showHideDescriptionField('jobSubFuncDescribe', 'none');
          // Clear jobSubFunction if it was 'Not Applicable' and now has actual options
          setFieldValues(prevValues => ({
             ...prevValues,
            [key]: value,
            jobSubFunction: '',
          }));
        }
      } else if (value === 'Prefer not to respond') {
        setJobSubFuncOptions([]);
        setDisableSubFunc(true);
        setJobSubFuncTxt('');
        showHideDescriptionField('jobSubFuncDescribe', 'none');
        // Auto-set jobSubFunction to 'Prefer not to respond' when parent is 'Prefer not to respond'
        setFieldValues(prevValues => ({
          ...prevValues,
          [key]: value,
          jobSubFunction: 'Prefer not to respond',
        }));
      }
      if (!value) {
        setDisableSubFunc(true);
      }
      return;
    }

    if (key === 'industry') {
      const options = checkSubDropDownValues(value, 'Industry Sector', globalState?.lookUpData);
      if (value && options && options?.length > 0) {
        setindustrySecTxt('');
        if (value === 'Other') {
          setIndustrySectOptions(otherOptionList);
          setDisableIndustrySec(false);
          if (fieldValues.industrySector && fieldValues.industrySector?.includes('~')) {
            setindustrySecTxt(fieldValues.industrySector.replace('OTH~', ''));
          }
          setFieldValues(prevValues => ({
            ...prevValues,
            [key]: value,
            industrySector: '',
          }));
        } else {
          setIndustrySectOptions(options);
          setDisableIndustrySec(false);
          showHideDescriptionField('industrySecDescribe', 'none');
          // Clear industrySector if it was 'Not Applicable' and now has actual options
          setFieldValues(prevValues => ({
            ...prevValues,
            [key]: value,
            industrySector: '',
          }));
        }
      } else if (value === 'Prefer not to respond') {
        setIndustrySectOptions([]);
        setDisableIndustrySec(true);
        setindustrySecTxt('');
        showHideDescriptionField('industrySecDescribe', 'none');
        // Auto-set industrySector to 'Prefer not to respond' when parent is 'Prefer not to respond'
        setFieldValues(prevValues => ({
          ...prevValues,
          [key]: value,
          industrySector: 'Prefer not to respond',
        }));
      }
      if (!value) {
        setDisableIndustrySec(true);
      }
      return;
    }

    // Special handling for countryOfResidence field
    // Clear dependent demographic fields when country changes if they're not applicable for the new country
    if (key === 'countryOfResidence') {
      const newCountry = value;
      const fieldsToCheck = ['ethnicity', 'lgbtq', 'veteranStatus', 'gender', 'age'];
      const updateFields: any = {};

      fieldsToCheck.forEach(fieldKey => {
        if (nonFilledFields.includes(fieldKey)) {
          // Clear ethnicity if new country is not in countryArrEth
          if ((fieldKey === 'ethnicity' || fieldKey === 'gender' || fieldKey === 'age') && newCountry && !countryArrEth.includes(newCountry)) {
            updateFields[fieldKey] = '';
          }
          // Clear lgbtq if new country is not in countryArrLGBTQ
          if (fieldKey === 'lgbtq' && newCountry && !countryArrLGBTQ.includes(newCountry)) {
            updateFields[fieldKey] = '';
          }
          // Clear veteranStatus if new country is not in countryArrVeteranStatus
          if (fieldKey === 'veteranStatus' && newCountry && !countryArrVeteranStatus.includes(newCountry)) {
            updateFields[fieldKey] = '';
          }
        }
      });

      // Apply cleared fields along with the country value update
      if (Object.keys(updateFields).length > 0) {
        setFieldValues(prevValues => ({
          ...prevValues,
          [key]: value,
          ...updateFields,
        }));
        return;
      }
    }

    // Date formatting is now handled directly in DatePicker onChange
    // No need to format here as it's already formatted when passed to this function
    setFieldValues(prevValues => ({
      ...prevValues,
      [key]: value,
    }));
  };

  /**
   * Effect to load client-specific branding and content
   *
   * This loads branding elements (welcome text, title, images, logos) based on:
   * 1. Client-specific configuration if available in clientInfo
   * 2. Branding values from the client data in global state
   * 3. Default values as a fallback
   */

  const applyClientBranding = () => {
    const info = clientInfo[title];
    setLeftPanelImg(info.leftImage);
    setHeaderLogo(info.headerLogo);
  };

  const applyGlobalBranding = () => {
    const leftPanelImgId = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_IMAGE');
    const headerLogoId = getBrandValues(globalState?.clientData?.branding, 'COMPANY_LOGO');

    if (globalState?.leftPanelImg) {
      setLeftPanelImg(globalState.leftPanelImg);
    } else {
      getBrandImages(leftPanelImgId, setLeftPanelImg, 'leftImage', dispatch, 'SET_LEFT_PANEL_IMG');
    }

    if (globalState?.headerLogo) {
      setHeaderLogo(globalState.headerLogo);
    } else {
      getBrandImages(headerLogoId, setHeaderLogo, 'headerLogo', dispatch, 'SET_HEADER_LOGO');
    }
  };

  useEffect(() => {
    if (Object.keys(globalState?.leaderData).length === 0) {
      navigate('/welcome');
    }

    if (title && clientInfo.hasOwnProperty(title)) {
      applyClientBranding();
    } else {
      applyGlobalBranding();
    }
  }, []);

  const downloadPrivacyPolicyPDF = () => {
    setShowDownloadLoader(true);
    if (!PRIVACY_POLICY_ATTACHMENTID) {
      console.error('REACT_APP_PRIVACY_POLICY_ATTACHMENTID is not defined in environment variables');
      setShowDownloadLoader(false);
      return;
    }

    Api.downloadAssessmentprivacyPolicyPDF(PRIVACY_POLICY_ATTACHMENTID)
      .then((res: any) => {
        if (res?.success) {
          setShowDownloadLoader(false);
        } else {
          setShowDownloadLoader(false);
          console.error('Failed to download Privacy Policy PDF');
        }
      })
      .catch((err: any) => {
        console.error('Error downloading Privacy Policy PDF:', err);
        setShowDownloadLoader(false);
      })
      .catch((err: any) => {
        setShowLoader(false);
        setShowDownloadLoader(false);
        if (err?.code === 401 && err?.message === 'Unauthorized Access') {
          dispatch({
            type: 'SET_SHOW_LOGOUT_MODAL',
            payload: true,
          });
        }
      });
  };

  const policyLabel = () => {
    return (
      <span className='policyLabelText' style={{ fontWeight: '500 !important' }}>
        By checking this box, you agree to our&nbsp;
        <Link
          classNames={`policyLink ${(showDownloadLoader)  ? 'disabled-link' : ''}`}
          rel='noopener noreferrer'
          underline
          variant='primary'
          onClick={(e: any) => {
            e.preventDefault();
            if (!showDownloadLoader) {
              downloadPrivacyPolicyPDF();
            }
          }}
        >
          {ASSESSMENT_POLICY_LINK_TEXT}
          {showDownloadLoader && <Icon path={IconName.mdiLoading} spin size={IconSize.Small} classNames='externalLinkIcon' />}
        </Link>
      </span>
    );
  };

  const validateFormData = (fieldsList:any) => {
    const customValidations = {};
    const commonFields = ['lgbtq', 'ethnicity', 'gender', 'age', 'veteranStatus'];
    let validateFieldList: Record<string, any> = { ...(fieldsList || {}) };

    const forDeletion:any = {
      'United Kingdom of Great Britain and Northern Ireland (the)': [
        'veteranStatus',
      ],
      'Canada': [
        'veteranStatus',
      ],
      'Australia': [
        'veteranStatus',
      ]
    };

    if (fieldsList?.countryOfResidence !== '' && !countryArrEth.includes(fieldsList.countryOfResidence)) {
      validateFieldList = Object.fromEntries(
        Object.entries(validateFieldList).filter(([item]) => !commonFields.includes(item))
      );
    } else if (fieldsList?.countryOfResidence !== 'United States of America (the)' && countryArrEth.includes(fieldsList.countryOfResidence)) {
      validateFieldList = Object.fromEntries(
        Object.entries(validateFieldList).filter(([item]) => !forDeletion[fieldsList?.countryOfResidence].includes(item))
      );
    }

    const fieldConfigs = Object.keys(validateFieldList).map((field: any) => {
      return { name: field, value: validateFieldList[field], type: fieldArr.find(f => f.key === field)?.type || 'textField' };
    });
    const isFormInvalid = validateDynamicForm(fieldConfigs, customValidations, setErrors);
    return isFormInvalid;
  };

  const validatePrivacyPolicy = () => {
    if (!privacyPolicyChecked) {
      setErrors((prev: any) => ({
        ...prev,
        privacyPolicyCheckedMsg: 'Please accept the privacy policy to continue'
      }));
      return true;
    }
  };

  const onClickContinue = () => {
    if (isPrivacyPolicyEnabled && validatePrivacyPolicy()) return;
    if (isDemographicEnabled && validateFormData(fieldValues)) return;
    
    if (fieldValues.jobSubFunction === otherTxt) {
      fieldValues.jobSubFunction = `OTH~${fieldValues.jobSubFuncDescribe.trim()}`;
    }
    if (fieldValues.industrySector === otherTxt) {
      fieldValues.industrySector = `OTH~${fieldValues.industrySecDescribe.trim()}`;
    }
    // Trim all text fields (including jobTitle) before sending
    const trimmedFieldValues = trimTextFields(fieldValues);
    const payload: any = {
      ...(isDemographicEnabled && {
        demographicSurveyData: {
          ...globalState?.leaderData?.demographicSurveyData,
          ...trimmedFieldValues,
        },
      }),
      ...(isPrivacyPolicyEnabled && privacyPolicyChecked && {
        applications: {
          leaderPortal: {
            isPrivacyPolicyAccepted: privacyPolicyChecked,
          },
        },
      }),
    };

    const updatedDataForContext: any = {
      ...(isDemographicEnabled && {
        demographicSurveyData: {
          ...globalState?.leaderData?.demographicSurveyData,
          ...trimmedFieldValues,
        },
      }),
      ...(isPrivacyPolicyEnabled && privacyPolicyChecked && {
        applications: {
          leaderPortal: {
            isPrivacyPolicyAccepted: privacyPolicyChecked,
          },
        },
      }),
    };
    const newContextData = { ...globalState?.leaderData, ...updatedDataForContext };
    const redirectUrl = '/dashboard';
    updateDemographicData({ payload, leaderProfileId: location.state.leaderProfileId, redirectUrl, setErrorMsg, newContextData, dispatch, navigate, setShowLoader });
    setShowLoader(true);
  };

  const handleTextfieldChange = (event:any) => {
    resolveError(event.target.id, errors, setErrors);
    setFieldValues(prevValues => ({
      ...prevValues,
      [event.target.id]: event.target.value,
    }));
  }

  const setItemRef = (id :string ) => (element:any) => {
    if (element) {
      itemRefs.current.set(id, element);
    } else {
      itemRefs.current.delete(id);
    }
  };

  /**
   * Validates that end date is after start date
   * Used by the DatePicker component to disable invalid dates
   *
   * @param {dayjs.Dayjs} current - The current date being evaluated
   * @returns {boolean} - True if date should be disabled, false otherwise
   */
  const disabledEndDate = (current: any) => {
    return current && current < dayjs(unformattedStartDate).endOf('day');
  };

  /**
   * Validates that start date is not in the future
   * Used by the DatePicker component to disable invalid dates
   *
   * @param {dayjs.Dayjs} current - The current date being evaluated
   * @returns {boolean} - True if date should be disabled, false otherwise
   */
  const disabledStartDate = (current: any) => {
    return current && current > dayjs().endOf('day');
  };

  return (
    <div className='mainWrapperOnboarding' style={{ backgroundImage: `url(${leftPanelImg})` }}>
      <div className='middleContentWrapper'>
        {/* Header with client logo */}
        <Header title={title} headerLogo={headerLogo} />
        <hr className='headerSeparator' />

        <div className='contentWrapper' style={{ padding: '40px 24px' }}>
          <div className='containerWrap'>
            <h2 className='welcomeBackTitle'>{WELCOME_BACK_TITLE}</h2>
            <p className='welcomeBackMessage'>{WELCOME_BACK_MESSAGE}</p>
          </div>

          {isPrivacyPolicyEnabled && (
            <div className='containerWrap'>
              <h2 className='privacyPolicyTitle' style={{marginBottom: '10px'}}>{UPDATED_PRIVACY_POLICY_TITLE}</h2>
              <p className='welcomeBackMessage'>{UPDATED_PRIVACY_POLICY_MESSAGE}</p>

              <div className='filedWrap-Link' style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CheckBox
                    ariaLabel='Label'
                    id='privacyPolicy'
                    labelAlign={LabelAlign.Center}
                    labelPosition={LabelPosition.End}
                    name='myCheckBoxName'
                    variant={SelectorVariant.Default}
                    classNames='formCheckbox'
                    onChange={e => {
                      setPrivacyPolicyCheck(e.target.checked);
                    }}
                  />
                  {policyLabel()}
                </div>
                {errors?.privacyPolicyCheckedMsg && <span className='errorMessage' style={{marginTop:'10px'}}>{errors?.privacyPolicyCheckedMsg}</span>}
              </div>
            </div>
          )}
          
          {isDemographicEnabled && (
            <div className='containerWrap'>
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <h3 className='privacyPolicyTitle'>{ADDITIONAL_ACCOUNT_INFO_TITLE}</h3>
                  <p className='welcomeBackMessage'>{ADDITIONAL_ACCOUNT_INFO_MESSAGE}</p>
                </div>
                
                
                {nonFilledFields.length > 0 &&
                  fieldArr
                    .filter(field => nonFilledFields.includes(field.key))
                    .filter(field => {
                      // Get current country value (check fieldValues first, then fallback to globalState)
                      const currentCountry = fieldValues['countryOfResidence'] || globalState?.leaderData?.demographicSurveyData?.countryOfResidence;

                      // Apply country-based filtering for specific fields
                      if ((field.key === 'ethnicity' || field.key === 'gender' || field.key === 'age') && currentCountry) {
                        return countryArrEth.includes(currentCountry);
                      }
                      if (field.key === 'lgbtq' && currentCountry) {
                        return countryArrLGBTQ.includes(currentCountry);
                      }
                      if (field.key === 'veteranStatus' && currentCountry) {
                        return countryArrVeteranStatus.includes(currentCountry);
                      }

                      // If country is not set yet and field requires country check, hide it
                      if (!currentCountry && ['ethnicity', 'lgbtq', 'veteranStatus', 'gender', 'age'].includes(field.key)) {
                        return false;
                      }

                      // Hide jobSubFunction if jobFunction is not selected, no options available, or "Prefer not to respond" is selected
                      if (field.key === 'jobSubFunction') {
                        const jobFunctionValue = fieldValues['jobFunction'] || globalState?.leaderData?.demographicSurveyData?.jobFunction;
                        if (!jobFunctionValue || jobFunctionValue === 'Prefer not to respond') {
                          return false;
                        }
                        return jobSubFuncOptions.length > 0;
                      }

                      // Hide industrySector if industry is not selected, no options available, or "Prefer not to respond" is selected
                      if (field.key === 'industrySector') {
                        const industryValue = fieldValues['industry'] || globalState?.leaderData?.demographicSurveyData?.industry;
                        if (!industryValue || industryValue === 'Prefer not to respond') {
                          return false;
                        }
                        return industrySectOptions.length > 0;
                      }

                      return true; // Show all other fields
                    })
                    .map(field => {
                      return (
                        <React.Fragment key={field.key}>
                          <div className='fieldWrap'>
                            <Label htmlFor={field.key} classNames='labelText' size={LabelSize.Medium} text={`${field.fieldName}`} />
                            {field.type === 'textField' ? (
                              <GenerateTextField 
                                fieldKey={field.key} 
                                fieldValue={fieldValues[field.key] || ''} 
                                errors={errors}
                                onChange={handleTextfieldChange}
                              />
                            ) : field.type === 'dropDown' ? (
                              <>
                              <select
                                id={field.key}
                                data-test-id={field.key}
                                value={fieldValues[field.key] || ''}
                                onChange={(e: any) => {
                                  handleFieldChange(field.key, e.target.value);
                                  if (field.key === 'jobSubFunction') {
                                    changeSubFunction(e.target.value);
                                  }
                                  if (field.key === 'industrySector') {
                                    changeIndustrySector(e.target.value);
                                  }
                                }}
                                className={`customSelectField selectField ${errors?.[`${field.key}Status`] === 'error' ? 'error' : ''}`}
                                disabled={field.key === 'jobSubFunction' ? disableSubFunc : field.key === 'industrySector' ? disableIndustrySec : false}
                              >
                                <option value='' disabled>Select</option>
                                {(field.key === 'jobSubFunction' ? jobSubFuncOptions : field.key === 'industrySector' ? industrySectOptions : dropdownOptions[field.key] || []).map((option: any) => (
                                  (option.value !== '' && option.value !== null && option.value !== undefined) ? (
                                    <option key={option.value} value={option.value}>
                                      {option.value}
                                    </option>
                                  ) : null
                                ))}
                              </select>
                              {errors?.[`${field.key}Msg`] && (
                                <span className='errorMessage'>{errors?.[`${field.key}Msg`]}</span>
                              )}
                              </>
                            ) : field.type === 'date' ? (
                              <>
                                <DatePicker
                                  id={field.key}
                                  size={DatePickerSize.Large}
                                  classNames='datePickerField'
                                  value={field.key === 'hireDate' ? unformattedStartDate : field.key === 'terminationDate' ? unformattedEndDate : fieldValues[field.key]}
                                  disabledDate={field.key === 'hireDate' ? disabledStartDate : field.key === 'terminationDate' ? disabledEndDate : undefined}
                                  onChange={(event:any, value: any) => {
                                    if (field.key === 'hireDate') {
                                      setUnformattedStartDate(dayjs(value));
                                      setStartDate(dayjs(value).format('YYYY-MM-DDTHH:mm:ss'));
                                      handleFieldChange(field.key, dayjs(value).format('YYYY-MM-DDTHH:mm:ss'));
                                    } else if (field.key === 'terminationDate') {
                                      setUnformattedEndDate(dayjs(value));
                                      setEndDate(dayjs(value).format('YYYY-MM-DDTHH:mm:ss'));
                                      handleFieldChange(field.key, dayjs(value).format('YYYY-MM-DDTHH:mm:ss'));
                                    } else {
                                      handleFieldChange(field.key, value);
                                    }
                                  }}
                                  status={errors?.[`${field.key}Status`]}
                                />
                                {errors?.[`${field.key}Msg`] && (
                                  <span className='errorMessage'>{errors?.[`${field.key}Msg`]}</span>
                                )}
                              </>
                            ) : null}
                          </div>
                          {(field.key === 'jobSubFunction' || field.key === 'industrySector') && (
                            <div 
                              className='fieldWrap' 
                              ref={setItemRef(field.key === 'jobSubFunction' ? 'jobSubFuncDescribe' : 'industrySecDescribe')} 
                              style={{display:'none'}}
                            >
                              <GenerateTextField 
                                fieldKey={field.key === 'jobSubFunction' ? 'jobSubFuncDescribe' : 'industrySecDescribe'} 
                                fieldValue={field.key === 'jobSubFunction' ? jobSubFuncTxt : industrySecTxt}
                                errors={errors}
                                style={{ display: 'none' }}
                                onChange={handleTextfieldChange}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })
                }
              </div>
            </div>
          )}

          <div className='containerWrap' style={{ marginBottom: '0px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                text={CONTINUE_BUTTON_TEXT}
                variant={ButtonVariant.Secondary}
                classNames='actionButton'
                style={actionButtonStyle}
                iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : { path: IconName.mdiArrowRight }}
                alignIcon={ButtonIconAlign.Right}
                disabled={showLoader}
                onClick={onClickContinue}
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default WelcomeBackPage;
